import React from 'react';
import { ImporterID } from 'fym-shared/src/music-services/ids';
import { serviceArtSize, serviceIconCrop } from 'fym-shared/src/music-services/serviceIconCrops';
import { musicServiceImagesByImporterId } from '../../../common/musicServiceImages';
import { TypedMemo } from '../../../common/types';
import { useSMLTheme } from '../../theme/useSMLTheme';
import { SMLTheme } from '../../theme/smlTheme';

// Map integrationType string to ImporterID
export const integrationTypeToImporterId: Record<string, ImporterID> = {
  spotify: ImporterID.Spotify,
  appleMusic: ImporterID.AppleMusic,
  youtube: ImporterID.Youtube,
  youtubeMusic: ImporterID.YouTubeMusic,
  deezer: ImporterID.Deezer,
  tidal: ImporterID.Tidal,
  amazonMusic: ImporterID.AmazonMusic,
  soundCloud: ImporterID.SoundCloud,
  pandora: ImporterID.Pandora,
  napster: ImporterID.Napster,
  qobuz: ImporterID.Qobuz,
  resso: ImporterID.Resso,
  boomplay: ImporterID.Boomplay,
  audiomack: ImporterID.Audiomack,
  audius: ImporterID.Audius,
  vkMusic: ImporterID.VK,
  yandexMusic: ImporterID.Yandex,
  qqMusic: ImporterID.QQMusic,
  zvuk: ImporterID.Zvuk,
  jiosaavn: ImporterID.JioSaavn,
  gaana: ImporterID.Gaana,
};

interface IntegrationTypeIconProps {
  integrationType: string;
  /** The side of the square icon slot, in px. */
  size?: number;
  /**
   * Keep every mark inside the square slot: a word logo (Amazon Music) is scaled down to
   * the slot width, not drawn in a wider slot. Use it where a fixed square box holds the icon.
   */
  square?: boolean;
  className?: string;
}

/** A word logo (Amazon Music) has no square icon, so it may be this many slots wide. */
const WORD_LOGO_WIDTH = 2.6;
/** The height of a word logo, as a share of the slot. */
const WORD_LOGO_HEIGHT = 0.6;

/** The rectangle and the drawn size of a service mark in a slot of `size`. */
export function integrationIconLayout(
  integrationType: string,
  theme: SMLTheme,
  size: number,
  square = false
): { viewBox?: string; width: number; height: number; isWide: boolean } {
  const crop = serviceIconCrop(integrationType);
  if (crop === undefined) {
    // No measured crop: the whole lockup, contained in a wide slot (or in the square slot).
    return square
      ? { width: size, height: size, isWide: false }
      : { width: Math.round(size * WORD_LOGO_WIDTH), height: size, isWide: true };
  }
  const rect = theme === 'dark' ? crop.dark : crop.light;
  const viewBox = `${rect.x} ${rect.y} ${rect.width} ${rect.height}`;
  if (!crop.isIcon) {
    const drawn = serviceArtSize(rect, false, size, {
      width: square ? size : size * WORD_LOGO_WIDTH,
      height: size * WORD_LOGO_HEIGHT,
    });
    return { viewBox, ...drawn, isWide: !square };
  }
  return { viewBox, ...serviceArtSize(rect, true, size, { width: size, height: size }), isWide: false };
}

/**
 * The brand icon of a music service in a square slot of `size` px.
 *
 * The logo files are lockups (the icon and the name). The icon alone is drawn: the crop of
 * `serviceIconCrops` becomes the viewBox, as on the Catalog cards. The ink follows the theme:
 * the `light` file (dark ink) on the light theme and the `dark` file (light ink) on the dark
 * theme, so Apple Music and Tidal show on both. A word logo (Amazon Music) gets a wider slot,
 * because it has no icon.
 */
const IntegrationTypeIconComponent: React.FC<IntegrationTypeIconProps> = ({
  integrationType,
  size = 20,
  square = false,
  className,
}) => {
  const { theme } = useSMLTheme();
  const importerId = integrationTypeToImporterId[integrationType];
  const images = importerId === undefined ? undefined : musicServiceImagesByImporterId[importerId];
  if (images === undefined) {
    return null;
  }
  const Logo = theme === 'dark' ? images.logoColor.dark : images.logoColor.light;

  const layout = integrationIconLayout(integrationType, theme, size, square);
  return (
    <span
      className={className}
      data-testid={`integration-icon-${integrationType}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: layout.isWide ? layout.width : size,
        height: size,
      }}
    >
      <Logo
        viewBox={layout.viewBox}
        width={layout.width}
        height={layout.height}
        style={{ flexShrink: 0, overflow: 'hidden' }}
      />
    </span>
  );
};

export const IntegrationTypeIcon = TypedMemo(IntegrationTypeIconComponent);
